// BKLS marketplace server functions. Client-safe module path — every handler
// body runs on the server only.
//
// Public reads go through the publishable-key client and are gated on
// status = 'published' AND workflow_status = 'published', so nothing in
// draft, review, scheduled, archived, or retired state can surface.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { publicClient } from "@/lib/shop.server";
import { topicsFor } from "@/lib/taxonomy";
import { TRENDING_WINDOW_DAYS, type BeatEngagement, type MarketplaceBeat } from "@/lib/bkls-marketplace";

const SlugSchema = z.string().trim().min(1).max(200).regex(/^[a-z0-9-]+$/);

const MARKETPLACE_COLUMNS =
  "id, slug, title, subtitle, description, bpm, musical_key, genre, mood, tags, duration_seconds, artwork_path, preview_path, collection_slug, sort_order, published_at, updated_at, created_at";

type Row = Record<string, any>;

async function hydrate(rows: Row[]): Promise<MarketplaceBeat[]> {
  if (!rows.length) return [];
  const supabase = publicClient();
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { signPath, ARTWORK_BUCKET, PREVIEW_TTL_SECONDS } = await import("@/lib/bkls.server");

  const { data: prices } = await (supabase.from("beat_tier_prices") as any)
    .select("beat_id, tier_key, price_cents, available")
    .in(
      "beat_id",
      rows.map((r) => r.id),
    );

  return Promise.all(
    rows.map(async (row) => ({
      id: row.id,
      slug: row.slug,
      title: row.title,
      subtitle: row.subtitle,
      description: row.description,
      bpm: row.bpm,
      musical_key: row.musical_key,
      genre: row.genre,
      mood: row.mood,
      tags: (row.tags ?? []) as string[],
      duration_seconds: row.duration_seconds,
      artworkUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.artwork_path, PREVIEW_TTL_SECONDS),
      previewUrl: await signPath(supabaseAdmin, ARTWORK_BUCKET, row.preview_path, PREVIEW_TTL_SECONDS),
      prices: ((prices ?? []) as Row[])
        .filter((p) => p.beat_id === row.id)
        .map((p) => ({
          tier_key: String(p.tier_key),
          price_cents: p.price_cents ?? null,
          available: p.available !== false,
        })),
      collectionSlug: row.collection_slug ?? null,
      publishedAt: row.published_at ?? row.created_at ?? null,
      updatedAt: row.updated_at ?? null,
      // Topics are detected from the beat's own author-written tags and copy.
      topics: topicsFor(
        { tags: row.tags ?? [], text: [row.title, row.subtitle, row.description].filter(Boolean).join(" ") },
        4,
      ).map((t) => t.slug),
    })),
  );
}

/** Every publicly published beat, enriched for marketplace discovery. */
export const listMarketplaceBeats = createServerFn({ method: "GET" }).handler(
  async (): Promise<MarketplaceBeat[]> => {
    const supabase = publicClient();
    const { data, error } = await (supabase.from("beats") as any)
      .select(MARKETPLACE_COLUMNS)
      .eq("status", "published")
      .eq("workflow_status", "published")
      .order("sort_order", { ascending: true })
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return hydrate((data ?? []) as Row[]);
  },
);

/**
 * Measured engagement per published beat over the trending window.
 * Counts are aggregated server-side and returned without any visitor
 * identifier, referrer, or country attached.
 */
export const getBeatEngagement = createServerFn({ method: "GET" }).handler(
  async (): Promise<BeatEngagement[]> => {
    const supabase = publicClient();
    const { data: published } = await (supabase.from("beats") as any)
      .select("slug")
      .eq("status", "published")
      .eq("workflow_status", "published");
    const live = new Set(((published ?? []) as Row[]).map((r) => r.slug as string));
    if (!live.size) return [];

    const since = new Date(Date.now() - TRENDING_WINDOW_DAYS * 86_400_000).toISOString();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await (supabaseAdmin.from("beat_analytics_events") as any)
      .select("beat_slug, event_type")
      .gte("created_at", since)
      .in("beat_slug", Array.from(live))
      .limit(50_000);

    const byslug = new Map<string, BeatEngagement>();
    for (const row of (data ?? []) as Row[]) {
      const slug = row.beat_slug as string;
      if (!live.has(slug)) continue;
      const entry = byslug.get(slug) ?? { slug, views: 0, previews: 0, licenseClicks: 0 };
      if (row.event_type === "view") entry.views += 1;
      else if (row.event_type === "preview_play" || row.event_type === "preview_complete") entry.previews += 1;
      else if (row.event_type === "license_click") entry.licenseClicks += 1;
      byslug.set(slug, entry);
    }
    return Array.from(byslug.values());
  },
);

/**
 * Peak envelope for a published beat's preview, for the waveform player.
 * Returns only the normalized numeric envelope — no storage path, checksum,
 * or master-file metadata.
 */
export const getBeatWaveform = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: SlugSchema }).parse(input))
  .handler(async ({ data }): Promise<{ peaks: number[] }> => {
    const supabase = publicClient();
    const { data: beat } = await (supabase.from("beats") as any)
      .select("id")
      .eq("slug", data.slug)
      .eq("status", "published")
      .eq("workflow_status", "published")
      .maybeSingle();
    if (!beat) return { peaks: [] };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: files } = await (supabaseAdmin.from("beat_files") as any)
      .select("kind, waveform, sort_order")
      .eq("beat_id", (beat as Row).id)
      .order("sort_order", { ascending: true });

    // Envelopes are stored either as a bare array or as { peaks, points,
    // version }; the master (wav) row holds the measured envelope when the
    // preview row only carries generation metadata.
    const peaksOf = (value: unknown): number[] => {
      const raw = Array.isArray(value)
        ? value
        : value && typeof value === "object" && Array.isArray((value as Row).peaks)
          ? ((value as Row).peaks as unknown[])
          : [];
      return raw.map((n) => (typeof n === "number" ? Math.max(0, Math.min(1, n)) : 0));
    };

    const rows = ((files ?? []) as Row[]).filter((f) => peaksOf(f.waveform).length);
    const preferred =
      rows.find((f) => f.kind === "preview") ??
      rows.find((f) => f.kind === "mp3") ??
      rows.find((f) => f.kind === "wav") ??
      rows[0];
    if (!preferred) return { peaks: [] };
    return { peaks: peaksOf(preferred.waveform).slice(0, 400) };
  });
